import React from 'react';
import SearchIcon from '../../Images/search-icon.svg';
import ArrowUp from '../../Images/chevron-up.svg';
import ArrowDown from '../../Images/chevron-down.svg';
import type { AddUserFormData, CustomerData, Plant, SelectionMap, Site } from './addUserTypes';

type Props = {
    formData: AddUserFormData;
    searchText: string;
    displayCustomers: CustomerData[];
    expandedItems: SelectionMap;
    onSearch: (value: string) => void;
    onSelectAll: (checked: boolean) => void;
    getTotalCount: () => number;
    isCustomerSelected: (customerId: string) => boolean;
    isSiteSelected: (siteId: string) => boolean;
    isPlantSelected: (plantId: string) => boolean;
    onCustomerSelect: (customerId: string, checked: boolean) => void;
    onSiteSelect: (siteId: string, customerId: string, checked: boolean) => void;
    onPlantSelect: (plantId: string, siteId: string, customerId: string, checked: boolean) => void;
    onToggleExpand: (id: string) => void;
};

const ExpandToggle: React.FC<{ expanded: boolean; onToggle: () => void }> = ({ expanded, onToggle }) => (
    <button type="button" className="expand-button" onClick={(e) => { e.preventDefault(); onToggle(); }}>
        {expanded ? (
            <img className="arrow-up" src={ArrowUp} alt="sort" />
        ) : (
            <img className="arrow-down" src={ArrowDown} alt="sort" />
        )}
    </button>
);

const PlantVisibilityCheckbox: React.FC<{
    plant: Plant;
    siteId: string;
    customerId: string;
    isPlantSelected: (plantId: string) => boolean;
    onPlantSelect: (plantId: string, siteId: string, customerId: string, checked: boolean) => void;
}> = ({ plant, siteId, customerId, isPlantSelected, onPlantSelect }) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onPlantSelect(plant.plantId, siteId, customerId, e.target.checked);
    };

    return (
        <label className="checkbox-container">
            <input
                type="checkbox"
                checked={isPlantSelected(plant.plantId)}
                onChange={handleChange}
            />
            <span className="checkbox-label">{plant.plantName}</span>
        </label>
    );
};

const SiteVisibilityRow: React.FC<{
    site: Site;
    customerId: string;
    expandedItems: SelectionMap;
    isSiteSelected: (siteId: string) => boolean;
    isPlantSelected: (plantId: string) => boolean;
    onSiteSelect: (siteId: string, customerId: string, checked: boolean) => void;
    onPlantSelect: (plantId: string, siteId: string, customerId: string, checked: boolean) => void;
    onToggleExpand: (id: string) => void;
}> = ({
    site,
    customerId,
    expandedItems,
    isSiteSelected,
    isPlantSelected,
    onSiteSelect,
    onPlantSelect,
    onToggleExpand,
}) => {
    const plantCount = site.plants?.length || 0;
    const siteHasPlants = plantCount > 0;
    const siteTooltipText = siteHasPlants ? '' : 'No plants available for selection';

    const handleSiteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (siteHasPlants) {
            onSiteSelect(site.siteId, customerId, e.target.checked);
        }
    };

    return (
        <div>
            <div className="checkbox-container">
                <input
                    type="checkbox"
                    checked={siteHasPlants ? isSiteSelected(site.siteId) : false}
                    onChange={handleSiteChange}
                    disabled={!siteHasPlants}
                    title={siteTooltipText}
                />
                <span className={`checkbox-label ${!siteHasPlants ? 'disabled' : ''}`}>
                    {site.siteName}
                </span>
                <div className="item-controls">
                    <span className="item-count" title={siteTooltipText}>
                        {plantCount}
                    </span>
                    {siteHasPlants ? (
                        <ExpandToggle
                            expanded={Boolean(expandedItems[site.siteId])}
                            onToggle={() => onToggleExpand(site.siteId)}
                        />
                    ) : (
                        <div className="expand-button-placeholder"></div>
                    )}
                </div>
            </div>

            {siteHasPlants && expandedItems[site.siteId] && (
                <div className="plant-list">
                    {site.plants.map((plant) => (
                        <PlantVisibilityCheckbox
                            key={plant.plantId}
                            plant={plant}
                            siteId={site.siteId}
                            customerId={customerId}
                            isPlantSelected={isPlantSelected}
                            onPlantSelect={onPlantSelect}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const CustomerVisibilityRow: React.FC<{
    customer: CustomerData;
    expandedItems: SelectionMap;
    isCustomerSelected: (id: string) => boolean;
    isSiteSelected: (id: string) => boolean;
    isPlantSelected: (id: string) => boolean;
    onCustomerSelect: (customerId: string, checked: boolean) => void;
    onSiteSelect: (siteId: string, customerId: string, checked: boolean) => void;
    onPlantSelect: (plantId: string, siteId: string, customerId: string, checked: boolean) => void;
    onToggleExpand: (id: string) => void;
}> = ({
    customer,
    expandedItems,
    isCustomerSelected,
    isSiteSelected,
    isPlantSelected,
    onCustomerSelect,
    onSiteSelect,
    onPlantSelect,
    onToggleExpand,
}) => {
    const hasAnyPlants = customer.sitesAndPlants.some(site => site.plants?.length > 0);
    const totalPlantCount = customer.sitesAndPlants.reduce(
        (total, site) => total + (site.plants?.length || 0),
        0
    );
    const customerTooltipText = hasAnyPlants ? '' : 'No plants available for selection';
    const customerExpanded = Boolean(expandedItems[customer.customerId]);

    const handleCustomerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (hasAnyPlants) {
            onCustomerSelect(customer.customerId, e.target.checked);
        }
    };

    return (
        <div className="customer-section">
            <div className="checkbox-container">
                <input
                    type="checkbox"
                    checked={hasAnyPlants ? isCustomerSelected(customer.customerId) : false}
                    onChange={handleCustomerChange}
                    disabled={!hasAnyPlants}
                    title={customerTooltipText}
                />
                <span className={`checkbox-label ${!hasAnyPlants ? 'disabled' : ''}`}>
                    {customer.customerName}
                    {customer.soldTo ? ` (#${customer.soldTo})` : ''}
                </span>
                <div className="item-controls">
                    <span className="item-count" title={customerTooltipText}>
                        {totalPlantCount}
                    </span>
                    {customer.sitesAndPlants.length > 0 ? (
                        <ExpandToggle
                            expanded={customerExpanded}
                            onToggle={() => onToggleExpand(customer.customerId)}
                        />
                    ) : (
                        <div className="expand-button-placeholder"></div>
                    )}
                </div>
            </div>

            {customer.sitesAndPlants.length > 0 && customerExpanded && (
                <div className="site-list">
                    {customer.sitesAndPlants.map((site) => (
                        <SiteVisibilityRow
                            key={site.siteId}
                            site={site}
                            customerId={customer.customerId}
                            expandedItems={expandedItems}
                            isSiteSelected={isSiteSelected}
                            isPlantSelected={isPlantSelected}
                            onSiteSelect={onSiteSelect}
                            onPlantSelect={onPlantSelect}
                            onToggleExpand={onToggleExpand}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

const AddUserAssetVisibility: React.FC<Props> = ({
    formData,
    searchText,
    displayCustomers,
    expandedItems,
    onSearch,
    onSelectAll,
    getTotalCount,
    isCustomerSelected,
    isSiteSelected,
    isPlantSelected,
    onCustomerSelect,
    onSiteSelect,
    onPlantSelect,
    onToggleExpand,
}) => (
    <div className="form-section">
        <h3 className="section-header">Asset Visibility</h3>
        <div className="search-container">
            <img src={SearchIcon} alt="search" className="search-icon" />
            <input
                type="text"
                placeholder="Search by customer, site or plant"
                className="search-input"
                value={searchText}
                onChange={(e) => onSearch(e.target.value)}
            />
        </div>

        <div className="visibility-options-scroll-container">
            <div className="visibility-options">
                {formData.userType === 'Internal' && (
                    <>
                        <label className="checkbox-container">
                            <input type="checkbox" onChange={(e) => onSelectAll(e.target.checked)} />
                            <span className="checkbox-label">All Customers & Sites</span>
                            <div className="item-controls">
                                <span className="item-count">{getTotalCount()}</span>
                            </div>
                        </label>
                        <div className='checkbox-label-all-divider'></div>
                    </>
                )}

                {displayCustomers.map((customer) => (
                    <CustomerVisibilityRow
                        key={customer.customerId}
                        customer={customer}
                        expandedItems={expandedItems}
                        isCustomerSelected={isCustomerSelected}
                        isSiteSelected={isSiteSelected}
                        isPlantSelected={isPlantSelected}
                        onCustomerSelect={onCustomerSelect}
                        onSiteSelect={onSiteSelect}
                        onPlantSelect={onPlantSelect}
                        onToggleExpand={onToggleExpand}
                    />
                ))}
            </div>
        </div>
    </div>
);

export default AddUserAssetVisibility;
